import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, map, catchError, of, shareReplay, tap } from 'rxjs';
import { ApiService, ApiResponse } from './api.service';
import { normalizeModuleKey } from './auth.service';

/** Shape used by Access Rights permission matrix (from AppModule/list). */
export interface AppModuleDto {
  appModuleId: string;
  appModuleName: string;
  displayText: string;
}

/** Response shape from AppModule/list. */
interface AppModuleListPayload {
  items?: { appModuleId: string; appModuleName: string; displayText: string; moduleType?: number }[];
  data?: { appModuleId: string; appModuleName: string; displayText: string; moduleType?: number }[];
}

export interface AccessRightListRequest {
  pageNumber: number;
  pageSize: number;
  searchTerm: string | null;
  sortColumn: string;
  sortDirection: string;
}

/** Per-module permissions returned from API (e.g. GetById / Login). */
export interface AccessRightModuleDto {
  moduleId: string;
  appModuleName: string;
  displayText: string;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  canView: boolean;
  canExport: boolean;
}

/** Sent on save - maps each module to permissions (AccessModuleRightMappingDto). */
export interface AccessModuleRightMappingDto {
  accessModuleRightId?: string | null;
  accessRightId?: string | null;
  appModuleName: string;
  /** @deprecated Resolved server-side from appModuleName. */
  moduleId?: string;
  canEdit: boolean;
  canAdd: boolean;
  canDelete: boolean;
  canView: boolean;
  canExport: boolean;
}

export interface AccessRightDto {
  accessRightId: string | null;
  accessRightName: string;
  createdBy?: string;
  isDeleted?: boolean;
  /** Read-only view from API (e.g. GetById). */
  modules?: AccessRightModuleDto[];
  /** Sent on save - list of module mappings for the backend. */
  accessModuleRightMappings?: AccessModuleRightMappingDto[];
}

export interface AccessRightListData {
  data?: AccessRightDto[];
  items?: AccessRightDto[];
  totalRecords?: number;
  totalCount?: number;
  pageNumber?: number;
  pageSize?: number;
}

/** Fallback so "Module Management" always appears in the permission matrix when not yet returned by API. */
const MODULE_MANAGEMENT_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'ModuleManagement',
  displayText: 'Module Management',
};

/** Fallback so "Category" always appears in the permission matrix when not yet returned by API. */
const CATEGORY_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'Category',
  displayText: 'Category',
};

/** Fallback so "Customers" always appears in the permission matrix when not yet returned by API. */
const CUSTOMERS_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'Customers',
  displayText: 'Customers',
};

/** Fallback so "Suppliers" always appears in the permission matrix when not yet returned by API. */
const SUPPLIERS_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'Suppliers',
  displayText: 'Suppliers',
};

/** Fallback so "Purity" always appears in the permission matrix when not yet returned by API. */
const PURITY_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'Purity',
  displayText: 'Purity',
};

/** Fallback so "Inventory" appears in permission matrix when not yet returned by API. */
const INVENTORY_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'Inventory',
  displayText: 'Inventory',
};

/** Fallback so "Sales" appears in permission matrix when not yet returned by API. */
const SALES_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'Sales',
  displayText: 'Sales',
};

const LABEL_IN_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'LabelGenerationLabelIn',
  displayText: 'Label Generation - Label In',
};

const ASSIGN_MARGIN_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'LabelGenerationAssignMargin',
  displayText: 'Label Generation - Assign Margin',
};

const GENERATE_LABEL_FALLBACK: AppModuleDto = {
  appModuleId: '',
  appModuleName: 'LabelGenerationGenerateLabel',
  displayText: 'Label Generation - Generate Label',
};

const APP_MODULES_STORAGE_KEY = 'sj_app_modules';

@Injectable({
  providedIn: 'root',
})
export class AccessRightService {
  private apiService = inject(ApiService);
  private platformId = inject(PLATFORM_ID);
  private appModulesCache$: Observable<AppModuleDto[]> | null = null;

  getAccessRights(
    request: AccessRightListRequest,
  ): Observable<ApiResponse<AccessRightListData>> {
    return this.apiService.post<AccessRightListData>('AccessRight/list', request);
  }

  getById(accessRightId: string): Observable<ApiResponse<AccessRightDto>> {
    return this.apiService.get<AccessRightDto>(`AccessRight/${accessRightId}`);
  }

  saveAccessRight(dto: AccessRightDto): Observable<ApiResponse<any>> {
    const payload = {
      ...dto,
      accessModuleRightMappings: dto.accessModuleRightMappings ?? [],
    };
    return this.apiService.post<any>('AccessRight/save', payload);
  }

  deleteAccessRight(accessRightId: string): Observable<ApiResponse<any>> {
    return this.apiService.delete<any>(`AccessRight/${accessRightId}`);
  }

  /** Cached app modules — sessionStorage on refresh; API only when cache is empty or after login. */
  getAppModules(): Observable<AppModuleDto[]> {
    if (!this.appModulesCache$) {
      const stored = this.loadModulesFromSessionStorage();
      if (stored) {
        this.appModulesCache$ = of(stored).pipe(shareReplay(1));
      } else {
        this.appModulesCache$ = this.fetchAppModulesFromApi().pipe(
          tap((modules) => this.saveModulesToSessionStorage(modules)),
          shareReplay(1),
        );
      }
    }
    return this.appModulesCache$;
  }

  /** Fetch fresh modules once after login and persist for the session. */
  refreshAppModulesFromApi(): void {
    this.appModulesCache$ = null;
    this.removeModulesFromSessionStorage();
    this.appModulesCache$ = this.fetchAppModulesFromApi().pipe(
      tap((modules) => this.saveModulesToSessionStorage(modules)),
      shareReplay(1),
    );
    this.appModulesCache$.subscribe();
  }

  /** Clear in-memory and session caches on logout. */
  clearAppModulesCache(): void {
    this.appModulesCache$ = null;
    this.removeModulesFromSessionStorage();
  }

  private loadModulesFromSessionStorage(): AppModuleDto[] | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    try {
      const raw = sessionStorage.getItem(APP_MODULES_STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as AppModuleDto[];
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
    } catch {
      return null;
    }
  }

  private saveModulesToSessionStorage(modules: AppModuleDto[]): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    try {
      sessionStorage.setItem(APP_MODULES_STORAGE_KEY, JSON.stringify(modules));
    } catch {
      // Ignore quota / private-mode errors.
    }
  }

  private removeModulesFromSessionStorage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    sessionStorage.removeItem(APP_MODULES_STORAGE_KEY);
  }

  private fetchAppModulesFromApi(): Observable<AppModuleDto[]> {
    const request = {
      pageNumber: 1,
      pageSize: 100,
      sortColumn: 'AppModuleName',
      sortDirection: 'ASC' as const,
    };
    return this.apiService.post<AppModuleListPayload>('AppModule/list', request).pipe(
      map((response) => {
        if (!response.success || !response.data) return [];
        const raw = response.data.items ?? response.data.data ?? [];
        let list = raw.map((m) => ({
          appModuleId: m.appModuleId ?? '',
          appModuleName: m.appModuleName ?? '',
          displayText: m.displayText ?? m.appModuleName ?? '',
        }));
        const hasModuleManagement = list.some(
          (m) =>
            (m.appModuleName && m.appModuleName.toLowerCase().includes('module management')) ||
            (m.displayText && m.displayText.toLowerCase().includes('module management')),
        );
        if (!hasModuleManagement) {
          list = [...list, MODULE_MANAGEMENT_FALLBACK];
        }
        const hasCategory = list.some(
          (m) =>
            (m.appModuleName && m.appModuleName.toLowerCase().includes('category')) ||
            (m.displayText && m.displayText.toLowerCase().includes('category')),
        );
        if (!hasCategory) {
          list = [...list, CATEGORY_FALLBACK];
        }
        const hasCustomers = list.some(
          (m) =>
            (m.appModuleName && m.appModuleName.toLowerCase().includes('customer')) ||
            (m.displayText && m.displayText.toLowerCase().includes('customer')),
        );
        if (!hasCustomers) {
          list = [...list, CUSTOMERS_FALLBACK];
        }
        const hasSuppliers = list.some(
          (m) =>
            (m.appModuleName && m.appModuleName.toLowerCase().includes('supplier')) ||
            (m.displayText && m.displayText.toLowerCase().includes('supplier')),
        );
        if (!hasSuppliers) {
          list = [...list, SUPPLIERS_FALLBACK];
        }
        const hasPurity = list.some(
          (m) =>
            (m.appModuleName && m.appModuleName.toLowerCase().includes('purity')) ||
            (m.displayText && m.displayText.toLowerCase().includes('purity')),
        );
        if (!hasPurity) {
          list = [...list, PURITY_FALLBACK];
        }
        const hasInventory = list.some(
          (m) =>
            (m.appModuleName && normalizeModuleKey(m.appModuleName) === 'inventory') ||
            (m.displayText && normalizeModuleKey(m.displayText) === 'inventory'),
        );
        if (!hasInventory) {
          list = [...list, INVENTORY_FALLBACK];
        }
        const hasSales = list.some(
          (m) =>
            normalizeModuleKey(m.appModuleName) === 'sales' ||
            normalizeModuleKey(m.displayText) === 'sales',
        );
        if (!hasSales) {
          list = [...list, SALES_FALLBACK];
        }
        const hasLabelIn = list.some(
          (m) => normalizeModuleKey(m.appModuleName) === 'labelgenerationlabelin',
        );
        if (!hasLabelIn) {
          list = [...list, LABEL_IN_FALLBACK];
        }
        const hasAssignMargin = list.some(
          (m) => normalizeModuleKey(m.appModuleName) === 'labelgenerationassignmargin',
        );
        if (!hasAssignMargin) {
          list = [...list, ASSIGN_MARGIN_FALLBACK];
        }
        const hasGenerateLabel = list.some(
          (m) => normalizeModuleKey(m.appModuleName) === 'labelgenerationgeneratelabel',
        );
        if (!hasGenerateLabel) {
          list = [...list, GENERATE_LABEL_FALLBACK];
        }
        return list;
      }),
      catchError(() => of([])),
    );
  }
}

